// PRO-127: shared sort helper for league-level player-prop listings.
//
// Sort keys (priority high-to-low):
//   1. matchedVolume DESC   (only when useVolume=true; 4casters)
//   2. start ASC            (chronological; invalid/missing sinks to bottom of tier)
//   3. per-sport category rank ASC  (unknown sinks to bottom of time bucket)
//   4. eventName alpha, case-insensitive (deterministic fallback)

const NBA_RANKS = [
  // Check specificity first: PRA / DOUBLE+DOUBLE / 3-point-like BEFORE plain POINT/REBOUND/ASSIST.
  { rank: 3, test: (s) => s.includes('P+R+A') || s.includes('PTS+REBS+ASTS') },
  { rank: 6, test: (s) => s.includes('DOUBLE+DOUBLE') },
  { rank: 5, test: (s) => s.includes('3 POINT') || s.includes('3-POINT') || s.includes('THREES MADE') },
  { rank: 2, test: (s) => s.includes('REBOUND') },
  { rank: 4, test: (s) => s.includes('ASSIST') },
  { rank: 1, test: (s) => s.includes('POINT') },
];

const NFL_RANKS = [
  { rank: 1, test: (s) => s.includes('(TOTAL RECEIVING YARDS)') },
  { rank: 2, test: (s) => s.includes('(TOTAL RUSHING YARDS)') },
  { rank: 3, test: (s) => s.includes('(TOTAL PASSING YARDS)') },
  { rank: 4, test: (s) => s.includes('(TOTAL TOUCHDOWN PASSES)') },
];

const NCAAF_RANKS = [
  { rank: 1, test: (s) => s.includes('(TOTAL RUSHING YARDS)') },
  { rank: 2, test: (s) => s.includes('(TOTAL RECEIVING YARDS)') },
  { rank: 3, test: (s) => s.includes('(TOTAL PASSING YARDS)') },
  { rank: 4, test: (s) => s.includes('(TOTAL TOUCHDOWN PASSES)') },
];

const MLB_RANKS = [
  { rank: 1, test: (s) => s.includes('STRIKEOUTS') },
  { rank: 2, test: (s) => s.includes('PITCHING OUTS') },
  { rank: 3, test: (s) => s.includes('TOTAL BASES') },
];

function pickTable(sport, league) {
  const leagueUpper = (league || '').toUpperCase();
  if (sport === 'basketball') return NBA_RANKS;
  if (sport === 'football') return leagueUpper.includes('NCAA') ? NCAAF_RANKS : NFL_RANKS;
  if (sport === 'baseball') return MLB_RANKS;
  return null;
}

export function getPropRank(eventName, sport, league) {
  const table = pickTable(sport, league);
  if (!table) return Number.POSITIVE_INFINITY;
  const upper = (eventName || '').toUpperCase();
  for (const entry of table) {
    if (entry.test(upper)) return entry.rank;
  }
  return Number.POSITIVE_INFINITY;
}

// PRO-186: secondary sort modes triggered by clicking column headers above
// the player-prop list. Default is POPULAR (today's volume-ranked output).
// PLAYER and STARTING are user toggles — exclusive, non-persistent, and reset
// to POPULAR on every full reload or main-pill change.
export const SORT_MODES = Object.freeze({
  POPULAR: 'popular',
  PLAYER: 'player',
  STARTING: 'startingSoon',
});

export function sortPropsList(
  gamesList,
  {
    sport,
    league,
    mode = SORT_MODES.POPULAR,
    direction = 'asc',
    useVolume,
  } = {},
) {
  // PRO-186: direction flips comparator output for user-driven modes
  // (PLAYER / STARTING). POPULAR keeps its native ordering — descending
  // "Popular" is meaningless.
  const flip = direction === 'desc' ? -1 : 1;

  // PRO-186 user-driven modes are intentionally single-key. JS Array.sort
  // is stable since ES2019 across every browser we support, so returning 0
  // on a tie preserves the caller's input order — which is the upstream
  // Popular (volume DESC) order. The user expectation: "Start Time only
  // sorts by start time. If a batch of games kicks off at the same minute,
  // the order I already saw stays put."
  if (mode === SORT_MODES.PLAYER) {
    return [...gamesList].sort(
      (a, b) =>
        flip *
        (a.eventName || '').localeCompare(b.eventName || '', undefined, {
          sensitivity: 'base',
        }),
    );
  }

  if (mode === SORT_MODES.STARTING) {
    return [...gamesList].sort((a, b) => {
      const aStart = Date.parse(a.start) || Number.POSITIVE_INFINITY;
      const bStart = Date.parse(b.start) || Number.POSITIVE_INFINITY;
      if (aStart === bStart) return 0;
      return flip * (aStart - bStart);
    });
  }

  // POPULAR (default). Pre-PRO-186 callers pass useVolume directly; new
  // callers pass mode and let it decide. Explicit useVolume always wins
  // (back-compat).
  const includeVolume =
    typeof useVolume === 'boolean' ? useVolume : true;

  return [...gamesList].sort((a, b) => {
    if (includeVolume) {
      const aVol = parseFloat(a.matchedVolume) || 0;
      const bVol = parseFloat(b.matchedVolume) || 0;
      if (aVol !== bVol) return bVol - aVol;
    }

    // Invalid / missing start sinks to the BOTTOM of its tier, not to 1970.
    const aStart = Date.parse(a.start) || Number.POSITIVE_INFINITY;
    const bStart = Date.parse(b.start) || Number.POSITIVE_INFINITY;
    if (aStart !== bStart) return aStart - bStart;

    const aRank = getPropRank(a.eventName, sport, league);
    const bRank = getPropRank(b.eventName, sport, league);
    if (aRank !== bRank) return aRank - bRank;

    return (a.eventName || '').localeCompare(b.eventName || '', undefined, {
      sensitivity: 'base',
    });
  });
}

// PRO-186: outer-only sort for grouped views (By Player / By Game). Inner
// ordering inside each group is the caller's responsibility — passed in
// already-sorted. POPULAR returns input order unchanged so callers preserve
// today's outer ordering as the default.
export function sortGroupsByMode(
  groups,
  {
    mode = SORT_MODES.POPULAR,
    direction = 'asc',
    getLabel,
    getEarliestStart,
  } = {},
) {
  const list = [...groups];
  const flip = direction === 'desc' ? -1 : 1;
  if (mode === SORT_MODES.PLAYER) {
    return list.sort(
      (a, b) =>
        flip *
        getLabel(a).localeCompare(getLabel(b), undefined, {
          sensitivity: 'base',
        }),
    );
  }
  if (mode === SORT_MODES.STARTING) {
    // Single-key, stable. Same-time groups keep the caller's input order
    // (Popular by default) instead of getting reshuffled alphabetically.
    return list.sort((a, b) => {
      const aStart = getEarliestStart(a);
      const bStart = getEarliestStart(b);
      if (aStart === bStart) return 0;
      return flip * (aStart - bStart);
    });
  }
  return list;
}
